// The Seal (决胜一刻, TECH_SPEC §4bis.22) — the giant stamp held over the death
// frame on a win or photo-finish loss, with the single share ask on the peak.
//
// This module is presentation only: every decision (whether to mount, which
// pool, the clamps, the fail behavior, every timeline number) is a pure
// function or constant in src/peak.ts, tested without a browser. The loop is
// already stopped when this mounts, so the canvas underneath holds the death
// frame by itself.
//
// Timeline (wall-clock, constants in src/peak.ts):
//   t=0        mount (transparent — the death frame is the subject)
//   t=250 ms   stamp punches in (scale 1.18 → 1, 180 ms cubic ease-out);
//              vibrate [30,40,30] on a win only
//   t=400 ms   tap-anywhere-to-dismiss arms (the lockout eats panic taps)
//   t=1000 ms  dismiss hint shows; the CTA slides up IF the submit has already
//              returned a shareCode — the row is HIDDEN until then (a visible
//              but dead ask at the peak reads as a broken promise, DR-R1)
//   t≈1200 ms  the CTA input-arms, only after its slide-up completes
//   t=4500 ms  no shareCode yet → auto-dismiss (a hanging request may never
//              hold the death frame indefinitely, DR-R2)

import {
  PEAK_CTA_MS,
  PEAK_CTA_SLIDE_MS,
  PEAK_STAMP_MS,
  PEAK_SUBMIT_CEILING_MS,
  PEAK_TAP_ARM_MS,
  onSubmitFail,
} from '../peak';
import { el } from './dom';

export interface PeakOptions {
  /** Already picked, interpolated and clamped by the caller (session.ts). */
  stamp: string;
  /** True → one type step down (the line exceeded its locale clamp). */
  small: boolean;
  /** Win gets the haptic; a near loss does not. */
  win: boolean;
  /**
   * Optional small line under the stamp (§4bis.28 duel seals use it: the stamp
   * is the verdict 「活过了」, the line is the receipt 「他倒在 {d} 米…」).
   * Absent on a §4bis.22 peak seal, whose stamp already carries the number.
   */
  line?: string;
  ctaLabel: string;
  dismissHint: string;
  /** CTA tap (only ever fires armed + share-ready). Caller shares + dismisses. */
  onShare: () => void;
  /** Tap-anywhere, the fail path and the submit ceiling all land here. */
  onDismiss: () => void;
}

export class PeakOverlay {
  private readonly screen: HTMLElement;
  private readonly ctaRow: HTMLElement;
  private readonly ctaBtn: HTMLButtonElement;
  private readonly hint: HTMLElement;
  private readonly timers: ReturnType<typeof setTimeout>[] = [];

  private shareReady = false;
  private ctaRevealed = false;
  private ctaArmed = false;
  private tapArmed = false;
  private done = false;

  constructor(
    root: HTMLElement,
    private readonly opts: PeakOptions,
  ) {
    const stamp = el('div', {
      class: opts.small ? 'peak-stamp peak-stamp--small' : 'peak-stamp',
      text: opts.stamp,
    });
    this.ctaBtn = el('button', {
      class: 'btn btn--primary peak-share',
      text: opts.ctaLabel,
      attrs: { type: 'button' },
    });
    this.ctaBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.ctaArmed && this.shareReady) this.opts.onShare();
    });
    this.hint = el('div', { class: 'peak-hint', text: opts.dismissHint });
    this.ctaRow = el('div', { class: 'peak-cta' }, [this.ctaBtn]);

    this.screen = el('div', { class: 'screen screen--peak' }, [
      el('div', { class: 'peak-spacer' }),
      stamp,
      opts.line === undefined ? null : el('div', { class: 'peak-line', text: opts.line }),
      el('div', { class: 'peak-spacer' }),
      this.ctaRow,
      this.hint,
    ]);
    this.screen.addEventListener('click', () => {
      if (this.tapArmed) this.dismiss();
    });
    root.append(this.screen);

    this.at(PEAK_STAMP_MS, () => {
      stamp.classList.add('is-stamped');
      if (opts.win && typeof navigator.vibrate === 'function') navigator.vibrate([30, 40, 30]);
    });
    this.at(PEAK_TAP_ARM_MS, () => {
      this.tapArmed = true;
    });
    this.at(PEAK_CTA_MS, () => {
      this.hint.classList.add('is-in');
      if (this.shareReady) this.revealCta();
    });
    this.at(PEAK_SUBMIT_CEILING_MS, () => {
      if (!this.shareReady) this.dismiss();
    });
  }

  /** The submit came back with a shareCode: the ask is honest now. Reveals the
   *  CTA immediately when past the 1 s mark, else the timer picks it up. */
  armShare(label?: string): void {
    if (this.done || this.shareReady) return;
    this.shareReady = true;
    // §4bis.31: the CTA is not even VISIBLE until this call, so the submit's
    // answer about which verb this run can offer arrives before the player has
    // seen a label at all. Relabelling here therefore shows one verb, once —
    // never a 帮我 that is withdrawn, and never a 打败我 that turns into one
    // under the reader's eyes.
    if (label !== undefined) this.ctaBtn.textContent = label;
    if (this.hint.classList.contains('is-in')) this.revealCta();
  }

  /** The submit failed — offline, refused, or unsaved (src/submitOutcome.ts):
   *  no shareCode in any of the three, so the ask can never be honest.
   *  Decision table in src/peak.ts. */
  submitFailed(): void {
    if (this.done) return;
    if (onSubmitFail(this.ctaRevealed) === 'dismiss') {
      this.dismiss();
      return;
    }
    this.ctaRow.remove(); // downgrade: the hint is already showing
    this.ctaArmed = false;
  }

  remove(): void {
    this.done = true;
    for (const t of this.timers) clearTimeout(t);
    this.timers.length = 0;
    this.screen.remove();
  }

  private revealCta(): void {
    if (this.ctaRevealed) return;
    this.ctaRevealed = true;
    this.ctaRow.classList.add('is-in');
    // Arms only after the 200 ms slide-up: a post-lockout masher must not land
    // an accidental share sheet (which would also inflate moment=peak).
    this.at(PEAK_CTA_SLIDE_MS, () => {
      this.ctaArmed = true;
    });
  }

  private dismiss(): void {
    if (this.done) return;
    this.opts.onDismiss();
  }

  private at(ms: number, fn: () => void): void {
    this.timers.push(setTimeout(fn, ms));
  }
}
